import {
  type ComponentType,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  Suspense,
  cloneElement,
  isValidElement,
  lazy,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { isNativeButtonElement } from '../layers/base';
import { mergeRefs } from './refs';

/** Triggers that render a native `<button>` (keyboard activation comes for free). */
export const NATIVE_BUTTON_COMPONENTS: Set<unknown> = new Set();

/** Load a lazy module once the browser is idle after first paint (or right away when asked). */
export function prefetchOnIdle(load: () => Promise<unknown>): void {
  if (typeof window === 'undefined') return;
  const run = () => void load().catch(() => undefined);
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
  if (idle) idle(run);
  else setTimeout(run, 200);
}

export type TriggerOptions = {
  trigger: ReactElement;
  open: boolean;
  /** Toggle request from the trigger (click, Enter/Space on non-button triggers). */
  onToggle: (via: 'pointer' | 'keyboard') => void;
  /** Arrow keys on a menu trigger open the menu at the first or last item. */
  onArrow?: (which: 'first' | 'last') => void;
  haspopup: 'dialog' | 'menu' | 'listbox';
  popupId: string;
  nativeButton?: boolean;
  /** Start loading the popup code (hover, focus). */
  prefetch: () => void;
};

/**
 * The trigger of a lazily loaded popup: the caller's own element (never wrapped, so it is the same DOM
 * node before and after the popup code loads) with toggle, ARIA and prefetch wired in. Its handlers
 * run before ours; `preventDefault()` in them cancels the toggle.
 */
export function useLayerTrigger(options: TriggerOptions): {
  element: ReactElement;
  triggerEl: HTMLElement | null;
} {
  const { trigger, open, onToggle, onArrow, haspopup, popupId, nativeButton, prefetch } = options;
  const [triggerEl, setTriggerEl] = useState<HTMLElement | null>(null);
  const setRef = useCallback((node: HTMLElement | null) => setTriggerEl(node), []);
  useEffect(() => {
    if (open) prefetch();
  }, [open, prefetch]);
  if (!isValidElement(trigger)) throw new Error('A popup trigger must be a React element');
  const own = trigger.props as Record<string, unknown>;
  const native = nativeButton ?? isNativeButtonElement(trigger, NATIVE_BUTTON_COMPONENTS);
  const call = (name: string, e: unknown) => (own[name] as ((ev: unknown) => void) | undefined)?.(e);
  const element = cloneElement(trigger, {
    ref: mergeRefs<HTMLElement>(own.ref, setRef),
    'aria-haspopup': haspopup,
    'aria-expanded': open,
    'aria-controls': open ? popupId : undefined,
    'data-popup-open': open ? '' : undefined,
    ...(native
      ? {}
      : {
          role: (own.role as string | undefined) ?? 'button',
          tabIndex: (own.tabIndex as number | undefined) ?? 0,
        }),
    onClick: (e: MouseEvent<HTMLElement>) => {
      call('onClick', e);
      if (e.defaultPrevented) return;
      onToggle(e.detail === 0 ? 'keyboard' : 'pointer');
    },
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      call('onKeyDown', e);
      if (e.defaultPrevented) return;
      if (!native && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onToggle('keyboard');
      } else if (onArrow && !open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
        onArrow(e.key === 'ArrowDown' ? 'first' : 'last');
      }
    },
    onPointerEnter: (e: unknown) => {
      call('onPointerEnter', e);
      prefetch();
    },
    onFocus: (e: unknown) => {
      call('onFocus', e);
      prefetch();
    },
  } as Record<string, unknown>);
  return { element, triggerEl };
}

/**
 * A popup implementation loaded on demand. Once loaded it renders synchronously (no Suspense frame);
 * a popup that mounted while loading stays on the lazy path for its lifetime (no remount mid-open).
 */
export function lazyPopup<P extends object>(load: () => Promise<{ default: ComponentType<P> }>) {
  let loaded: ComponentType<P> | null = null;
  let pending: Promise<void> | null = null;
  const ensure = (): Promise<void> => {
    pending ??= load().then(
      (m) => {
        loaded = m.default;
      },
      (error: unknown) => {
        pending = null;
        throw error;
      },
    );
    return pending;
  };
  const Lazy = lazy(async () => {
    await ensure();
    return { default: loaded! };
  });
  function Popup(props: P) {
    const [sync] = useState(() => loaded !== null);
    const Impl = loaded;
    if (sync && Impl) return <Impl {...props} />;
    return (
      <Suspense fallback={null}>
        <Lazy {...props} />
      </Suspense>
    );
  }
  // Unit tests render and click in the same tick: load right away there.
  if (import.meta.env?.MODE === 'test') void ensure().catch(() => undefined);
  return { Popup, prefetch: () => void ensure().catch(() => undefined), load: ensure };
}

/**
 * Keeps a popup mounted while it animates out: `mounted` turns true with `open` and false once the
 * popup reports its exit finished.
 */
export function useMountedWhileOpen(open: boolean): [boolean, () => void] {
  const [mounted, setMounted] = useState(open);
  if (open && !mounted) setMounted(true);
  const exited = useCallback(() => setMounted(false), []);
  return [mounted || open, exited];
}
